import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, FormListFieldData, Input, InputNumber, Row, Select, Space, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { StatePieceStatus } from '../../../enums';
import { IAddInvoice } from '../../../interfaces/invoices/data/IAddInvoice';
import { IAddInvoiceFormValues } from '../../../interfaces/invoices/IAddInvoiceFormValues';
import { ContentHeader } from '../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { AppDispatch, RootState } from '../../../store';
import { addInvoices } from '../../../store/invoiceSlice/InvoiceActions';
import './AddInvoice.less';
import { Unauthorized } from '../../../shared/components/unauthorized/Unauthorized';
import { ROW_GUTTER, DECIMAL_SEPARATOR } from '../../../shared/Constants';
import { fetchFeeById } from '../../../store/feeSlice/FeeActions';
import { fetchCustomersLookupList } from '../../../store/lookupSlice/LookupActions';

export const AddInvoice = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { detailFee } = useSelector((state: RootState) => state.feeSlice);
    const customerSlice = useSelector((state: RootState) => state.customerSlice);
    const invoiceSlice = useSelector((state: RootState) => state.invoiceSlice);
    const feeSlice = useSelector((state: RootState) => state.feeSlice);
    const appSlice = useSelector((state: RootState) => state.appSlice);
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const [isValidFeeId, setIsValidFeeId] = useState(false);
    const [feeIdFieldIsTouched, setFeeIdFieldIsTouched] = useState(false);
    const [form] = Form.useForm();
    const isLoadingData =
        feeSlice.fees.fetchStatus === StatePieceStatus.IsFetching || customerSlice.customers.fetchStatus === StatePieceStatus.IsFetching;

    const onFinish = (values: { fees: IAddInvoiceFormValues[] }) => {
        const fees = values.fees as IAddInvoice[];
        const body: IAddInvoice[] = [...fees];
        dispatch(addInvoices(body));
    };

    const validateFeeId = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(fetchFeeById(parseInt(e.target.value)));

        if (!feeIdFieldIsTouched) {
            setFeeIdFieldIsTouched(true);
        }
    };

    useEffect(() => {
        if (invoiceSlice.detailPage.invoice.createStatus === StatePieceStatus.Success) {
            navigate('/invoices', { replace: true });
        }
    }, [invoiceSlice.detailPage.invoice.createStatus]);

    useEffect(() => {
        let isValid = detailFee.fee.fetchStatus === StatePieceStatus.Success;
        setIsValidFeeId(isValid);
    }, [detailFee.fee.fetchStatus]);

    useEffect(() => {
        form.setFieldsValue({
            fees: [
                {
                    feeId: null,
                    amount: null,
                    reasonOfPayment: null,
                    contactPerson: null,
                    customerId: null,
                },
            ],
        });
        dispatch(fetchCustomersLookupList());
    }, []);

    if (appSlice.auth.validationCompleted && !appSlice.auth.invoices.create) {
        return <Unauthorized />;
    }

    return (
        <React.Fragment>
            <Form
                form={form}
                name="invoice-add"
                onFinish={onFinish}
                layout="vertical"
                onValuesChange={(changedValue: any, values) => {
                    form.setFieldsValue(values);
                }}
            >
                <ContentHeader
                    title={`Create a new invoice`}
                    breadcrumbs={[
                        {
                            breadcrumbName: 'Invoices',
                            path: '/invoices',
                        },
                        {
                            breadcrumbName: 'Create',
                            path: `/invoices/add`,
                        },
                    ]}
                >
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={invoiceSlice.detailPage.invoice.updateStatus === StatePieceStatus.IsFetching || isLoadingData}
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={invoiceSlice.detailPage.invoice.updateStatus === StatePieceStatus.IsFetching || isLoadingData}
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                </ContentHeader>

                <div className="page-content">
                    <Spin spinning={invoiceSlice.detailPage.invoice.fetchStatus === StatePieceStatus.IsFetching}>
                        <Form.List name="fees">
                            {(fields: FormListFieldData[]) => (
                                <>
                                    {fields.map((field, index) => (
                                        <React.Fragment key={index}>
                                            <Row>
                                                <Col span={24}>
                                                    <Space>
                                                        <label className="group-label">Invoice information</label>
                                                    </Space>
                                                </Col>
                                            </Row>
                                            <Row gutter={ROW_GUTTER}>
                                                <Col span={8}>
                                                    <label className="property required">Fee Id</label>
                                                    <Form.Item
                                                        {...field}
                                                        key={field.key}
                                                        name={[field.name, 'feeId']}
                                                        rules={[{ required: true, message: "'Fee Id' is required" }]}
                                                        validateStatus={!isValidFeeId && feeIdFieldIsTouched ? 'error' : undefined}
                                                        help={
                                                            !isValidFeeId && feeIdFieldIsTouched
                                                                ? "'Invalid 'fee Id' is provided."
                                                                : undefined
                                                        }
                                                    >
                                                        <Input placeholder="Enter a fee Id" onChange={validateFeeId} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <label className="property required">Customer</label>
                                                    <Form.Item
                                                        {...field}
                                                        key={field.key}
                                                        name={[field.name, 'customerId']}
                                                        rules={[{ required: true, message: "'Customer' is required" }]}
                                                    >
                                                        <Select
                                                            showSearch
                                                            placeholder="Select a customer"
                                                            filterOption={(input, option) =>
                                                                (option!.children as unknown as string)
                                                                    .toLowerCase()
                                                                    .startsWith(input.toLowerCase())
                                                            }
                                                        >
                                                            {lookupSlice.customers.data
                                                                .filter((f) => !f.isDeleted)
                                                                .map((c) => (
                                                                    <Select.Option key={c.key} value={c.key} disabled={c.isDeleted}>
                                                                        {c.value}
                                                                    </Select.Option>
                                                                ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <label className="property">Amount</label>
                                                    <Form.Item {...field} key={field.key} name={[field.name, 'amount']}>
                                                        <InputNumber decimalSeparator={DECIMAL_SEPARATOR}></InputNumber>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row gutter={ROW_GUTTER}>
                                                <Col span={8}>
                                                    <label className="property">Contact person</label>
                                                    <Form.Item
                                                        {...field}
                                                        key={field.key}
                                                        name={[field.name, 'contactPerson']}
                                                        rules={[{ max: 255 }]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <label className="property required">Reason of payment</label>
                                                    <Form.Item
                                                        {...field}
                                                        key={field.key}
                                                        name={[field.name, 'reasonOfPayment']}
                                                        rules={[{ required: true, message: "'Reason of Payment' is required", max: 255 }]}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>&nbsp;</Col>
                                            </Row>
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </Spin>
                </div>
            </Form>
        </React.Fragment>
    );
};
